import React, {
	useCallback,
	useEffect,
	useState,
	useMemo,
} from 'react';
import {
	Divider,
	Empty,
	Flex,
} from 'antd';
import {
	CargoContractSides,
	Currencies,
	FixtureCounterpartyTypes,
} from '@shared/utils/constants';
import { Values } from '@shared/utils/objectEnums';
import type
{
	CargoDetails,
	GetCargoDetailsResponse,
} from '@api/features/cargos/getCargoDetails';
import type {
	EstimatedExpenseItem,
	EstimatedRevenueItem,
} from '@api/models/cargo';
import { GetEstimateDetailsResponse } from '@api/features/estimates/getEstimateDetails';
import useFetchedState from '@client/utils/hooks/useFetchedState';
import {
	getCounterparties,
	getPorts,
	getUserData,
} from '@client/lib/api';
import EditableDetails from '@client/components/EditableDetails/EditableDetails';
import { getCargoDetailsFields } from '@client/screens/cargos/details/helpers/getCargoDetailsFields';
import { OnUpdateCargo } from '@client/screens/estimates/details/context/hooks/useCargoHandlers';
import { useExchangeRates } from '@client/utils/hooks/useExchangeRates';
import BrokerCommissionDetails from '../components/CommissionsCard/BrokerCommissionDetails';
import { getLinkedVcInCargo, isSyncedCargoKeyField } from '../helpers/cargoUtils';
import getKeyCargoDetailsFields from './helpers/getKeyCargoDetailsFields';
import styles from './CargoDetailsScreen.module.css';
import { ModalModes } from './CargoDetailsScreen';
import CargoObligationDetailsForm from './CargoObligationDetailsForm';

const CargoKeyDetails = ({
	cargoDetails,
	refreshCargoDetails,
	handleChange,
	isTceLocked,
	baseCurrency,
	mode,
	estimate,
}: {
	cargoDetails?: GetCargoDetailsResponse | null;
	refreshCargoDetails: () => void;
	isTceLocked?: boolean;
	handleChange: OnUpdateCargo;
	baseCurrency: Values<typeof Currencies>;
	mode: ModalModes;
	estimate: GetEstimateDetailsResponse | null;
}) => {
	const [userData] = useFetchedState(getUserData);
	const inEstimate = mode === 'estimate';
	const [ports] = useFetchedState(getPorts);
	const [charterers, _reloadCharterers, _error, _loading] = useFetchedState(
		() => getCounterparties(FixtureCounterpartyTypes.CHARTEREROWNER),
	);
	const [selectedCurrency, setSelectedCurrency] = useState<
		Values<typeof Currencies> | null | undefined
	>(cargoDetails?.currency);
	const exchangeRates = useExchangeRates(baseCurrency);

	useEffect(() => {
		setSelectedCurrency(cargoDetails?.currency);
	}, [cargoDetails]);

	useEffect(() => {
		if (
			cargoDetails != null && selectedCurrency != null &&
			selectedCurrency !== cargoDetails.currency
		) {
			const items = cargoDetails?.estimatedItems ?? [];

			cargoDetails.estimatedItems = items
				.map((item) => (
					{
						...item,
						currency: cargoDetails.currency,
						exchangeRate: exchangeRates[cargoDetails.currency],
					} as EstimatedRevenueItem | EstimatedExpenseItem
				));

			handleChange(cargoDetails.id, cargoDetails);
		}
	}, [cargoDetails, cargoDetails?.exchangeRate, exchangeRates, handleChange, selectedCurrency]);

	const handleCargoTypeSelect = useCallback((v: number) => {
		if (cargoDetails == null) {
			return;
		}

		handleChange(cargoDetails.id, {
			stowageFactor: v,
		});
	}, [cargoDetails?.id, handleChange]);

	const handleRateChange = useCallback(() => {
		if (cargoDetails != null) {
			handleChange(
				cargoDetails.id,
				{
					currency: cargoDetails.currency,
					exchangeRate: exchangeRates[cargoDetails.currency],
				},
			);
			setSelectedCurrency(cargoDetails.currency);
		}
	}, [cargoDetails, exchangeRates, handleChange]);

	const handleEdit = useCallback(async (values: GetCargoDetailsResponse) => {
		if (cargoDetails?.id == null) {
			return;
		}

		const [key, value] = Object.entries(values)[0];
		const field = key as keyof CargoDetails;

		if (cargoDetails?.[field] === value) {
			return;
		}

		if (key === 'currency' && typeof value === 'string') {
			values.currency = value as Values<typeof Currencies>;
			if (typeof exchangeRates === 'object' && value in exchangeRates) {
				values.exchangeRate = exchangeRates[value as Values<typeof Currencies>];
			}
		}

		handleChange(cargoDetails.id, values);

		const linkedVcInCargo = getLinkedVcInCargo(
			cargoDetails as CargoDetails,
			estimate?.cargos ?? [],
		);

		if (
			cargoDetails.contractSide === CargoContractSides.VC_OUT &&
			linkedVcInCargo != null &&
			isSyncedCargoKeyField(field)
		) {
			handleChange(linkedVcInCargo.id, values);
		}

	}, [cargoDetails, handleChange, exchangeRates, estimate?.cargos]);

	const items = useMemo(() => {
		if (cargoDetails == null) {
			return [];
		}

		return getKeyCargoDetailsFields({
			cargoDetails,
			charterers,
			isTceLocked,
			ports,
			handleRateChange,
			handleCargoTypeSelect,
			baseCurrency,
			mode,
			userData,
			estimate,
		});
	}, [baseCurrency, cargoDetails, charterers, handleRateChange, inEstimate, isTceLocked, ports, userData, estimate]);

	if (cargoDetails == null) {
		return (
			<Empty
				image={Empty.PRESENTED_IMAGE_SIMPLE}
				description="No data"
			/>
		);
	}

	return (
		<Flex vertical>
			<Divider>
				Key Details
			</Divider>
			<EditableDetails
				className={styles.keyDetailsTable}
				title="Details"
				// @ts-ignore
				items={items}
				// @ts-ignore
				onEditValuesChange={handleEdit}
				editing
				hideHeader
			/>
			<Divider>
				Other Details
			</Divider>
			<EditableDetails
				className={styles.keyDetailsTable}
				title="Details"
				items={getCargoDetailsFields({
					cargoDetails,
				})}
				// @ts-ignore
				onEditValuesChange={handleEdit}
				editing
				hideHeader
			/>
			<Divider>
				Broker Commissions
			</Divider>
			<BrokerCommissionDetails
				refreshCargoDetails={refreshCargoDetails}
				cargoDetails={cargoDetails}
				handleChange={handleChange}
			/>
			<Divider />
			<div className={styles.compactForm}>
				<CargoObligationDetailsForm 
					cargoDetails={cargoDetails}
					estimate={estimate}
					handleChange={handleChange}
					mode={mode}
				/>
			</div>
		</Flex>
	);
};

export default CargoKeyDetails;
